import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { toast } from 'react-hot-toast'
import { ApiResponse } from '@/types/api'
import i18n from '@/i18n'

// API base configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.kryzonic.com'

class ApiService {
  private client: AxiosInstance
  private refreshTokenPromise: Promise<string | null> | null = null

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Add auth token if available
        const token = this.getToken()
        console.log(`🔍 API 요청 인터셉터: ${config.method?.toUpperCase()} ${config.url}`)
        console.log('🔍 토큰 확인:', token ? `${token.substring(0, 50)}...` : 'null')
        console.log('🔍 config.headers 상태:', config.headers)
        
        // headers가 없으면 초기화
        if (!config.headers) {
          config.headers = {} as any
        }
        
        // Set Accept-Language header from i18n
        const lang = localStorage.getItem('partner_language') || 'ko'
        config.headers['Accept-Language'] = lang

        if (token) {
          config.headers.Authorization = `Bearer ${token}`
          console.log('✅ Authorization 헤더 추가됨:', config.headers.Authorization)
        } else {
          console.log('❌ 토큰이 없어서 Authorization 헤더 추가 안 됨')
        }

        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        const data = response.data as ApiResponse

        // Handle API response format
        if (data.result === 'error') {
          const errorMessage = data.message || 'An error occurred'
          toast.error(errorMessage)
          throw new Error(errorMessage)
        }

        return response
      },
      async (error) => {
        const response = error?.response
        const originalRequest = error?.config || {}

        if (!response) {
          toast.error(i18n.t('common:serverConnectionFailed'))
          return Promise.reject(error)
        }

        const { status, data } = response
        const url: string = (originalRequest.url || '') as string
        const isAuthEndpoint =
          url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/logout')

        if (status === 401 && !(originalRequest as any)._retry && !isAuthEndpoint) {
          ;(originalRequest as any)._retry = true
          try {
            const newAccessToken = await this.getOrRefreshAccessToken()
            if (newAccessToken) {
              originalRequest.headers = originalRequest.headers || {}
              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
              return this.client(originalRequest)
            }
            this.handleUnauthorized()
            return Promise.reject(error)
          } catch (e) {
            this.handleUnauthorized()
            return Promise.reject(e)
          }
        }

        // 로그인/인증 엔드포인트는 토스트를 표시하지 않음 (컴포넌트에서 처리)
        if (!isAuthEndpoint) {
          switch (status) {
            case 403:
              toast.error(i18n.t('common:noPermissionError'))
              break
            case 404:
              toast.error(i18n.t('common:resourceNotFound'))
              break
            case 500:
              toast.error(i18n.t('common:serverError'))
              break
            default: {
              const errorMessage = data?.message || i18n.t('common:requestFailed', { status })
              toast.error(errorMessage)
            }
          }
        }

        return Promise.reject(error)
      }
    )
  }

  // Token management
  private getToken(): string | null {
    const token = localStorage.getItem('partner_access_token')
    console.log('🔍 getToken() 호출 결과:', token ? `토큰 있음: ${token.substring(0, 50)}...` : '토큰 없음')
    return token
  }

  private setToken(token: string): void {
    localStorage.setItem('partner_access_token', token)
  }

  private clearToken(): void {
    localStorage.removeItem('partner_access_token')
    localStorage.removeItem('partner_refresh_token')
  }

  private handleUnauthorized(): void {
    try {
      this.clearToken()
      // Clear persisted auth store to prevent redirect loops
      localStorage.removeItem('partner-auth')
    } finally {
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem('partner_refresh_token')
  }

  private async getOrRefreshAccessToken(): Promise<string | null> {
    if (this.refreshTokenPromise) {
      return this.refreshTokenPromise
    }

    this.refreshTokenPromise = this.refreshAccessToken()
      .then((token) => {
        this.refreshTokenPromise = null
        return token
      })
      .catch((err) => {
        this.refreshTokenPromise = null
        throw err
      })

    return this.refreshTokenPromise
  }

  private async refreshAccessToken(): Promise<string | null> {
    const refreshToken = this.getRefreshToken()
    if (!refreshToken) {
      return null
    }

    try {
      // 인터셉터를 타지 않는 독립 요청으로 리프레시 수행
      const resp = await axios.post(
        `${API_BASE_URL}/api/v1/partners/auth/refresh`,
        { refresh_token: refreshToken },
        { headers: { 'Content-Type': 'application/json' } }
      )

      const data = resp.data as { access_token: string; refresh_token: string; token_type: string }
      if (data?.access_token) {
        this.setAuthTokens(data.access_token, data.refresh_token)
        return data.access_token
      }
      return null
    } catch (e) {
      return null
    }
  }

  // HTTP methods
  async get<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.client.get<ApiResponse<T>>(url, config)
    return response.data
  }

  async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    console.log('🔴 POST 요청:', url)
    console.log('🔴 POST 데이터:', data)
    console.log('🔴 POST config:', config)
    const response = await this.client.post<ApiResponse<T>>(url, data, config)
    return response.data
  }

  async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.client.put<ApiResponse<T>>(url, data, config)
    return response.data
  }

  async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.client.patch<ApiResponse<T>>(url, data, config)
    return response.data
  }

  async delete<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response = await this.client.delete<ApiResponse<T>>(url, config)
    return response.data
  }

  // File upload
  async uploadFile<T = any>(
    url: string,
    file: File,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await this.client.post<ApiResponse<T>>(url, formData, {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  }

  // Multiple file upload
  async uploadFiles<T = any>(
    url: string,
    files: File[],
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const formData = new FormData()
    files.forEach((file) => {
      formData.append(`files`, file)
    })

    const response = await this.client.post<ApiResponse<T>>(url, formData, {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  }

  // Form data submission
  async postFormData<T = any>(
    url: string,
    data: Record<string, any>,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const formData = new FormData()
    
    Object.entries(data).forEach(([key, value]) => {
      if (value instanceof File) {
        formData.append(key, value)
      } else if (Array.isArray(value)) {
        value.forEach((item, index) => {
          formData.append(`${key}[${index}]`, item)
        })
      } else if (value !== null && value !== undefined) {
        formData.append(key, String(value))
      }
    })

    const response = await this.client.post<ApiResponse<T>>(url, formData, {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  }

  // Auth methods
  setAuthTokens(accessToken: string, refreshToken?: string): void {
    this.setToken(accessToken)
    if (refreshToken) {
      localStorage.setItem('partner_refresh_token', refreshToken)
    }
  }

  clearAuthTokens(): void {
    this.clearToken()
  }

  isAuthenticated(): boolean {
    return !!this.getToken()
  }

  // Download file
  async downloadFile(
    url: string,
    filename?: string,
    config?: AxiosRequestConfig
  ): Promise<void> {
    const response = await this.client.get(url, {
      ...config,
      responseType: 'blob',
    })

    // Create download link
    const blob = new Blob([response.data])
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename || 'download'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  }

  // Get raw axios instance for special cases
  getClient(): AxiosInstance {
    return this.client
  }
}

// Create singleton instance
export const apiService = new ApiService()

// Export for convenience
export default apiService